import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useDeferredValue,
  memo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  FormControlLabel,
  Checkbox,
  Radio,
  Autocomplete,
  CircularProgress,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  importOrder,
  getOrderEntryCampaigns,
  getCampaignProducts,
  lookupCampaignCoupon,
} from "api/order-entry.api";
import type {
  OrderImportPayload,
  Campaign,
  CampaignProduct,
  CampaignCoupon,
  ShippingProfile,
} from "api/order-entry.api";
import { AxiosError } from "axios";
import type { ApiError } from "types";
import type { OrderEntryPrefill } from "types/orderEntryPrefill";
import { getOrderDetail, chargeOrder } from "api/recovery.api";
import { extractDeclinedCardInfo } from "utils/declinedOrderInfo";
import CreditCardIcon from "@mui/icons-material/CreditCard";

// --- Country list (CheckoutChamp-supported) ---
const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "GB", name: "United Kingdom" },
];

// State / province codes per country. These values are sent verbatim to the
// CheckoutChamp order/import API, so they must be the codes it expects.
const STATES_BY_COUNTRY: Record<string, { code: string; name: string }[]> = {
  US: [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "DC", name: "District of Columbia" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
    { code: "AS", name: "American Samoa" },
    { code: "FM", name: "Federated States of Micronesia" },
    { code: "GU", name: "Guam" },
    { code: "MP", name: "Northern Mariana Islands" },
    { code: "PR", name: "Puerto Rico" },
    { code: "MH", name: "Republic of Marshall Islands" },
    { code: "VI", name: "Virgin Islands of the U.S." },
    { code: "AA", name: "Armed Forces Americas" },
    { code: "AE", name: "Armed Forces Canada, Armed Forces Europe, Armed Forces Middle East, Armed Forces Africa" },
    { code: "AP", name: "Armed Forces Pacific" },
  ],
  CA: [
    { code: "AB", name: "Alberta" },
    { code: "BC", name: "British Columbia" },
    { code: "MB", name: "Manitoba" },
    { code: "NB", name: "New Brunswick" },
    { code: "NL", name: "Newfoundland and Labrador" },
    { code: "NT", name: "Northwest Territories" },
    { code: "NS", name: "Nova Scotia" },
    { code: "NU", name: "Nunavut" },
    { code: "ON", name: "Ontario" },
    { code: "PE", name: "Prince Edward Island" },
    { code: "QC", name: "Quebec" },
    { code: "SK", name: "Saskatchewan" },
    { code: "YT", name: "Yukon" },
  ],
  AU: [
    { code: "ACT", name: "Australian Capital Territory" },
    { code: "NSW", name: "New South Wales" },
    { code: "NT", name: "Northern Territory" },
    { code: "QLD", name: "Queensland" },
    { code: "SA", name: "South Australia" },
    { code: "TAS", name: "Tasmania" },
    { code: "VIC", name: "Victoria" },
    { code: "WA", name: "Western Australia" },
  ],
  GB: [
    { code: "ABE", name: "Aberdeen City" },
    { code: "ABD", name: "Aberdeenshire" },
    { code: "ANS", name: "Angus" },
    { code: "ANT", name: "Antrim" },
    { code: "ARD", name: "Ards" },
    { code: "AGB", name: "Argyll and Bute" },
    { code: "ARM", name: "Armagh" },
    { code: "BLA", name: "Ballymena" },
    { code: "BLY", name: "Ballymoney" },
    { code: "BNB", name: "Banbridge" },
    { code: "BDG", name: "Barking and Dagenham" },
    { code: "BNE", name: "Barnet" },
    { code: "BNS", name: "Barnsley" },
    { code: "BAS", name: "Bath and North East Somerset" },
    { code: "BBO", name: "Bedford Borough" },
    { code: "BDF", name: "Bedfordshire" },
    { code: "BFS", name: "Belfast" },
    { code: "BRK", name: "Berkshire" },
    { code: "BEX", name: "Bexley" },
    { code: "BIR", name: "Birmingham" },
    { code: "BBD", name: "Blackburn with Darwen" },
    { code: "BPL", name: "Blackpool" },
    { code: "BGW", name: "Blaenau Gwent" },
    { code: "BOL", name: "Bolton" },
    { code: "BMH", name: "Bournemouth" },
    { code: "BRC", name: "Bracknell Forest" },
    { code: "BRD", name: "Bradford" },
    { code: "BEN", name: "Brent" },
    { code: "BGE", name: "Bridgend" },
    { code: "BNH", name: "Brighton and Hove" },
    { code: "BST", name: "Bristol, City of" },
    { code: "BRY", name: "Bromley" },
    { code: "BUC", name: "Buckingham" },
    { code: "BKM", name: "Buckinghamshire" },
    { code: "BUR", name: "Bury" },
    { code: "CAY", name: "Caerphilly" },
    { code: "CLD", name: "Calderdale" },
    { code: "CAM", name: "Cambridgeshire" },
    { code: "CMD", name: "Camden" },
    { code: "CRF", name: "Cardiff" },
    { code: "CMN", name: "Carmarthenshire" },
    { code: "CKF", name: "Carrickfergus" },
    { code: "CSR", name: "Castlereagh" },
    { code: "CGN", name: "Ceredigion" },
    { code: "CHS", name: "Cheshire" },
    { code: "CHE", name: "Cheshire East" },
    { code: "CWC", name: "Cheshire West and Chester" },
    { code: "CLK", name: "Clackmannanshire" },
    { code: "CLR", name: "Coleraine" },
    { code: "CWY", name: "Conwy" },
    { code: "CKT", name: "Cookstown" },
    { code: "CON", name: "Cornwall" },
    { code: "COV", name: "Coventry" },
    { code: "CGV", name: "Craigavon" },
    { code: "CRY", name: "Croydon" },
    { code: "CMA", name: "Cumbria" },
    { code: "DAL", name: "Darlington" },
    { code: "DEN", name: "Denbighshire" },
    { code: "DER", name: "Derby" },
    { code: "DBY", name: "Derbyshire" },
    { code: "DRY", name: "Derry" },
    { code: "DEV", name: "Devon" },
    { code: "DNC", name: "Doncaster" },
    { code: "DOR", name: "Dorset" },
    { code: "DOW", name: "Down" },
    { code: "DUD", name: "Dudley" },
    { code: "DGY", name: "Dumfries and Galloway" },
    { code: "DND", name: "Dundee City" },
    { code: "DGN", name: "Dungannon" },
    { code: "DUR", name: "Durham" },
    { code: "EAL", name: "Ealing" },
    { code: "EAY", name: "East Ayrshire" },
    { code: "EDU", name: "East Dunbartonshire" },
    { code: "ELN", name: "East Lothian" },
    { code: "ERW", name: "East Renfrewshire" },
    { code: "ERY", name: "East Riding of Yorkshire" },
    { code: "ESX", name: "East Sussex" },
    { code: "EDH", name: "Edinburgh, City of" },
    { code: "ELS", name: "Eilean Siar" },
    { code: "ENF", name: "Enfield" },
    { code: "ESS", name: "Essex" },
    { code: "FAL", name: "Falkirk" },
    { code: "FER", name: "Fermanagh" },
    { code: "FIF", name: "Fife" },
    { code: "FLN", name: "Flintshire" },
    { code: "GAT", name: "Gateshead" },
    { code: "GLG", name: "Glasgow City" },
    { code: "GLS", name: "Gloucestershire" },
    { code: "GLO", name: "Greater London" },
    { code: "GRE", name: "Greenwich" },
    { code: "GGY", name: "Guernsey" },
    { code: "GWN", name: "Gwynedd" },
    { code: "HCK", name: "Hackney" },
    { code: "HAL", name: "Halton" },
    { code: "HMF", name: "Hammersmith and Fulham" },
    { code: "HAM", name: "Hampshire" },
    { code: "HRY", name: "Haringey" },
    { code: "HRW", name: "Harrow" },
    { code: "HPL", name: "Hartlepool" },
    { code: "HAV", name: "Havering" },
    { code: "HEF", name: "Herefordshire, County of" },
    { code: "HRT", name: "Hertfordshire" },
    { code: "HTF", name: "Hertfordshire" },
    { code: "HLD", name: "Highland" },
    { code: "HIL", name: "Hillingdon" },
    { code: "HNS", name: "Hounslow" },
    { code: "IVC", name: "Inverclyde" },
    { code: "AGY", name: "Isle of Anglesey" },
    { code: "IOM", name: "Isle of Man" },
    { code: "IOW", name: "Isle of Wight" },
    { code: "IOS", name: "Isles of Scilly" },
    { code: "ISL", name: "Islington" },
    { code: "JEY", name: "Jersey" },
    { code: "KEC", name: "Kensington and Chelsea" },
    { code: "KEN", name: "Kent" },
    { code: "KHL", name: "Kingston upon Hull, City of" },
    { code: "KTT", name: "Kingston upon Thames" },
    { code: "KIR", name: "Kirklees" },
    { code: "KWL", name: "Knowsley" },
    { code: "LBH", name: "Lambeth" },
    { code: "LAN", name: "Lancashire" },
    { code: "LRN", name: "Larne" },
    { code: "LDS", name: "Leeds" },
    { code: "LCE", name: "Leicester" },
    { code: "LEC", name: "Leicestershire" },
    { code: "LEW", name: "Lewisham" },
    { code: "LMV", name: "Limavady" },
    { code: "LIN", name: "Lincolnshire" },
    { code: "LSB", name: "Lisburn" },
    { code: "LIV", name: "Liverpool" },
    { code: "LND", name: "London, City of" },
    { code: "LUT", name: "Luton" },
    { code: "MFT", name: "Magherafelt" },
    { code: "MAN", name: "Manchester" },
    { code: "MDW", name: "Medway" },
    { code: "MSY", name: "Merseyside" },
    { code: "MTY", name: "Merthyr Tydfil" },
    { code: "MRT", name: "Merton" },
    { code: "MDB", name: "Middlesbrough" },
    { code: "MDX", name: "Middlesex" },
    { code: "MLN", name: "Midlothian" },
    { code: "MIK", name: "Milton Keynes" },
    { code: "MON", name: "Monmouthshire" },
    { code: "MRY", name: "Moray" },
    { code: "MYL", name: "Moyle" },
    { code: "NTL", name: "Neath Port Talbot" },
    { code: "NET", name: "Newcastle upon Tyne" },
    { code: "NWM", name: "Newham" },
    { code: "NWP", name: "Newport" },
    { code: "NYM", name: "Newry and Mourne" },
    { code: "NTA", name: "Newtownabbey" },
    { code: "NFK", name: "Norfolk" },
    { code: "NAY", name: "North Ayrshire" },
    { code: "NDN", name: "North Down" },
    { code: "NEL", name: "North East Lincolnshire" },
    { code: "NLK", name: "North Lanarkshire" },
    { code: "NLN", name: "North Lincolnshire" },
    { code: "NSM", name: "North Somerset" },
    { code: "NTY", name: "North Tyneside" },
    { code: "NYK", name: "North Yorkshire" },
    { code: "NTH", name: "Northamptonshire" },
    { code: "NBL", name: "Northumberland" },
    { code: "NGM", name: "Nottingham" },
    { code: "NTT", name: "Nottinghamshire" },
    { code: "OLD", name: "Oldham" },
    { code: "OMH", name: "Omagh" },
    { code: "ORK", name: "Orkney Islands" },
    { code: "OXF", name: "Oxfordshire" },
    { code: "PEM", name: "Pembrokeshire" },
    { code: "PKN", name: "Perth and Kinross" },
    { code: "PTE", name: "Peterborough" },
    { code: "PLY", name: "Plymouth" },
    { code: "POL", name: "Poole" },
    { code: "POR", name: "Portsmouth" },
    { code: "POW", name: "Powys" },
    { code: "RDG", name: "Reading" },
    { code: "RDB", name: "Redbridge" },
    { code: "RCC", name: "Redcar and Cleveland" },
    { code: "RFW", name: "Renfrewshire" },
    { code: "RCT", name: "Rhondda, Cynon, Ta" },
    { code: "RIC", name: "Richmond upon Thames" },
    { code: "RCH", name: "Rochdale" },
    { code: "ROT", name: "Rotherham" },
    { code: "RUT", name: "Rutland" },
    { code: "SLF", name: "Salford" },
    { code: "SAW", name: "Sandwell" },
    { code: "SCB", name: "Scottish Borders, The" },
    { code: "SFT", name: "Sefton" },
    { code: "SHF", name: "Sheffield" },
    { code: "ZET", name: "Shetland Islands" },
    { code: "SHR", name: "Shropshire" },
    { code: "SLG", name: "Slough" },
    { code: "SOL", name: "Solihull" },
    { code: "SOM", name: "Somerset" },
    { code: "SAY", name: "South Ayrshire" },
    { code: "SGC", name: "South Gloucestershire" },
    { code: "SLK", name: "South Lanarkshire" },
    { code: "STY", name: "South Tyneside" },
    { code: "SYK", name: "South Yorkshire" },
    { code: "STH", name: "Southampton" },
    { code: "SOS", name: "Southend-on-Sea" },
    { code: "SWK", name: "Southwark" },
    { code: "SHN", name: "St. Helens" },
    { code: "STS", name: "Staffordshire" },
    { code: "STG", name: "Stirling" },
    { code: "SKP", name: "Stockport" },
    { code: "STT", name: "Stockton-on-Tees" },
    { code: "STE", name: "Stoke-on-Trent" },
    { code: "STB", name: "Strabane" },
    { code: "SFK", name: "Suffolk" },
    { code: "SND", name: "Sunderland" },
    { code: "SRY", name: "Surrey" },
    { code: "STN", name: "Sutton" },
    { code: "SWA", name: "Swansea" },
    { code: "SWD", name: "Swindon" },
    { code: "TAM", name: "Tameside" },
    { code: "TFW", name: "Telford and Wrekin" },
    { code: "THR", name: "Thurrock" },
    { code: "TOB", name: "Torbay" },
    { code: "TOF", name: "Torfaen" },
    { code: "TWH", name: "Tower Hamlets" },
    { code: "TRF", name: "Trafford" },
    { code: "VGL", name: "Vale of Glamorgan, T" },
    { code: "WKF", name: "Wakefield" },
    { code: "WLL", name: "Walsall" },
    { code: "WFT", name: "Waltham Forest" },
    { code: "WND", name: "Wandsworth" },
    { code: "WRT", name: "Warrington" },
    { code: "WAR", name: "Warwickshire" },
    { code: "WBK", name: "West Berkshire" },
    { code: "WDU", name: "West Dunbartonshire" },
    { code: "WLN", name: "West Lothian" },
    { code: "WMD", name: "West Midlands" },
    { code: "WSX", name: "West Sussex" },
    { code: "WYK", name: "West Yorkshire" },
    { code: "WSM", name: "Westminster" },
    { code: "WGN", name: "Wigan" },
    { code: "WIL", name: "Wiltshire" },
    { code: "WNM", name: "Windsor and Maidenhead" },
    { code: "WRL", name: "Wirral" },
    { code: "WOK", name: "Wokingham" },
    { code: "WLV", name: "Wolverhampton" },
    { code: "WOC", name: "Worcester" },
    { code: "WOR", name: "Worcestershire" },
    { code: "WRX", name: "Wrexham" },
    { code: "YOR", name: "York" },
  ],
};

const schema = z
  .object({
    campaignId: z.string().min(1, "Campaign is required"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    companyName: z.string().optional(),
    emailAddress: z.string().email("Valid email is required"),
    phoneNumber: z.string().optional(),
    address1: z.string().min(1, "Address is required"),
    address2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State is required"),
    country: z.string().min(1, "Country is required"),
    postalCode: z.string().min(1, "Postal code is required"),
    sameAsShipping: z.boolean(),
    shipFirstName: z.string().optional(),
    shipLastName: z.string().optional(),
    shipAddress1: z.string().optional(),
    shipAddress2: z.string().optional(),
    shipCity: z.string().optional(),
    shipState: z.string().optional(),
    shipCountry: z.string().optional(),
    shipPostalCode: z.string().optional(),
    couponCode: z.string().optional(),
    notes: z.string().optional(),
    // Only meaningful/shown when completing a declined-order retry —
    // "external" is today's normal unpaid-lead + invoice flow, unchanged.
    paymentMethod: z.enum(["external", "credit_card"]),
    creditCardNumber: z.string().optional(),
    cvv: z.string().optional(),
    expirationMonth: z.string().optional(),
    expirationYear: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentMethod !== "credit_card") return;
    if (!data.creditCardNumber || data.creditCardNumber.replace(/\s/g, "").length < 13) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["creditCardNumber"], message: "Valid card number is required" });
    }
    if (!data.cvv || data.cvv.length < 3) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cvv"], message: "Valid security code is required" });
    }
    if (!data.expirationMonth) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expirationMonth"], message: "Required" });
    }
    if (!data.expirationYear) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["expirationYear"], message: "Required" });
    }
  });

type FormData = z.infer<typeof schema>;

const defaultValues: FormData = {
  campaignId: "",
  firstName: "",
  lastName: "",
  companyName: "",
  emailAddress: "",
  phoneNumber: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  country: "US",
  postalCode: "",
  sameAsShipping: true,
  shipFirstName: "",
  shipLastName: "",
  shipAddress1: "",
  shipAddress2: "",
  shipCity: "",
  shipState: "",
  shipCountry: "US",
  shipPostalCode: "",
  paymentMethod: "external",
  creditCardNumber: "",
  cvv: "",
  expirationMonth: "",
  expirationYear: "",
  couponCode: "",
  notes: "",
};

function resolveStateCode(country: string, rawState: string | undefined): string {
  if (!rawState) return "";
  const options = STATES_BY_COUNTRY[country] ?? [];
  const trimmed = rawState.trim();
  const byCode = options.find((s) => s.code.toLowerCase() === trimmed.toLowerCase());
  if (byCode) return byCode.code;
  const byName = options.find((s) => s.name.toLowerCase() === trimmed.toLowerCase());
  if (byName) return byName.code;
  return "";
}

function buildDefaultValues(prefill: OrderEntryPrefill | undefined): FormData {
  if (!prefill) return defaultValues;

  const billingCountry = prefill.billingCountry || defaultValues.country;
  const shippingCountry = prefill.shippingCountry || billingCountry;

  const addressesMatch =
    (prefill.billingAddress1 || "") === (prefill.shippingAddress1 || "") &&
    (prefill.billingCity || "") === (prefill.shippingCity || "") &&
    (prefill.billingState || "") === (prefill.shippingState || "") &&
    (prefill.billingCountry || "") === (prefill.shippingCountry || "") &&
    (prefill.billingPostalCode || "") === (prefill.shippingPostalCode || "");

  return {
    ...defaultValues,
    firstName: prefill.firstName || defaultValues.firstName,
    lastName: prefill.lastName || defaultValues.lastName,
    emailAddress: prefill.emailAddress || defaultValues.emailAddress,
    phoneNumber: prefill.phoneNumber || defaultValues.phoneNumber,
    address1: prefill.billingAddress1 || defaultValues.address1,
    city: prefill.billingCity || defaultValues.city,
    state: resolveStateCode(billingCountry, prefill.billingState) || defaultValues.state,
    country: billingCountry,
    postalCode: prefill.billingPostalCode || defaultValues.postalCode,
    sameAsShipping: addressesMatch,
    shipFirstName: prefill.firstName || defaultValues.shipFirstName,
    shipLastName: prefill.lastName || defaultValues.shipLastName,
    shipAddress1: prefill.shippingAddress1 || defaultValues.shipAddress1,
    shipCity: prefill.shippingCity || defaultValues.shipCity,
    shipState: resolveStateCode(shippingCountry, prefill.shippingState) || defaultValues.shipState,
    shipCountry: shippingCountry,
    shipPostalCode: prefill.shippingPostalCode || defaultValues.shipPostalCode,
    // Default to Credit Card when retrying a declined order — that's the
    // whole point of this flow. Agent can still switch back to External.
    paymentMethod: prefill.declinedOrderId ? "credit_card" : defaultValues.paymentMethod,
  };
}

// --- Product quantity state per offerId ---
interface OfferQty {
  [offerId: number]: number;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <InputLabel
      sx={{ mb: 0.5, fontWeight: 600, fontSize: "0.85rem", color: "text.primary" }}
    >
      {children}
      {required && <span style={{ color: "#d32f2f" }}> *</span>}
    </InputLabel>
  );
}

// --- Orange accent color to match screenshot ---
const ORANGE = "#e8700a";

// Every one of these used to be an object literal inside the row markup, so a
// fresh copy was allocated — and re-serialized by emotion — for all 50 visible
// rows on every single render of this page. Hoisting them makes them identity-
// stable, which is also what lets ProductRow's memo comparison actually hold.
const NAME_CELL_SX = { fontSize: "0.85rem", fontStyle: "italic" } as const;
const INFO_ICON_SX = {
  fontSize: 15,
  color: "#5ba3cf",
  verticalAlign: "middle",
  cursor: "pointer",
} as const;
const QTY_BOX_SX = { display: "inline-flex", alignItems: "center", gap: 0.5 } as const;
const QTY_BUTTON_SX = {
  bgcolor: ORANGE,
  color: "#fff",
  width: 28,
  height: 28,
  "&:hover": { bgcolor: "#c95e00" },
} as const;
const QTY_ICON_SX = { fontSize: 16 } as const;
const QTY_INPUT_PROPS = {
  style: { textAlign: "center" as const, width: 36, padding: "4px 0" },
};
const PRICE_INPUT_PROPS = {
  min: 0,
  step: "0.01",
  style: { textAlign: "center" as const, width: 64, padding: "4px 8px" },
};
const SHIPPING_INPUT_PROPS = {
  style: { textAlign: "center" as const, width: 60, padding: "4px 8px" },
};
const SHIPPING_READ_ONLY = { readOnly: true } as const;

interface ProductRowProps {
  product: CampaignProduct;
  qty: number;
  /** The agent's price override for this row, or undefined when untouched. */
  overridePrice: string | undefined;
  onQtyDelta: (campaignProductId: number, delta: number) => void;
  onQtySet: (campaignProductId: number, value: number) => void;
  onPriceChange: (campaignProductId: number, value: string) => void;
}

// One row per campaign product. Memoized deliberately: this page re-renders on
// every keystroke anywhere on the form — react-hook-form's watch() subscriptions,
// the qty/price maps, the coupon field — and without memo that rebuilt all ~24
// MUI components in each of the 50 visible rows (~1,200 components) every time.
// Props are primitives plus stable callbacks so a change to one row's qty or
// price re-renders that row only.
const ProductRow = memo(function ProductRow({
  product,
  qty,
  overridePrice,
  onQtyDelta,
  onQtySet,
  onPriceChange,
}: ProductRowProps) {
  const id = product.campaignProductId;
  const basePrice = parseFloat(product.price || "0").toFixed(2);
  const displayName =
    product.offerName ||
    product.offerLabel ||
    product.productName ||
    `Product ${product.productId}`;

  return (
    <TableRow hover>
      <TableCell sx={NAME_CELL_SX}>
        {displayName}{" "}
        <Tooltip
          title={`Product ID: ${product.productId}, Offer ID: ${id}`}
          arrow
        >
          <InfoOutlinedIcon sx={INFO_ICON_SX} />
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Box sx={QTY_BOX_SX}>
          <IconButton
            size="small"
            onClick={() => onQtyDelta(id, -1)}
            sx={QTY_BUTTON_SX}
          >
            <RemoveIcon sx={QTY_ICON_SX} />
          </IconButton>
          <TextField
            value={qty}
            onChange={(e) => onQtySet(id, parseInt(e.target.value, 10) || 0)}
            size="small"
            inputProps={QTY_INPUT_PROPS}
          />
          <IconButton
            size="small"
            onClick={() => onQtyDelta(id, 1)}
            sx={QTY_BUTTON_SX}
          >
            <AddIcon sx={QTY_ICON_SX} />
          </IconButton>
        </Box>
      </TableCell>
      <TableCell align="center">
        <TextField
          value={overridePrice ?? basePrice}
          onChange={(e) => onPriceChange(id, e.target.value)}
          onBlur={(e) => {
            const n = parseFloat(e.target.value);
            onPriceChange(
              id,
              Number.isNaN(n) || n < 0 ? basePrice : n.toFixed(2)
            );
          }}
          size="small"
          type="number"
          inputProps={PRICE_INPUT_PROPS}
        />
      </TableCell>
      <TableCell align="center">
        <TextField
          value={parseFloat(product.shippingPrice || "0").toFixed(2)}
          size="small"
          InputProps={SHIPPING_READ_ONLY}
          inputProps={SHIPPING_INPUT_PROPS}
        />
      </TableCell>
    </TableRow>
  );
});

export default function OrderEntryPage() {
  const location = useLocation();
  const prefill = (location.state as { prefill?: OrderEntryPrefill } | null)?.prefill;
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [successSeverity, setSuccessSeverity] = useState<"success" | "warning">("success");
  const [offerQtys, setOfferQtys] = useState<OfferQty>({});
  const [offerPrices, setOfferPrices] = useState<Record<number, string>>({});
  const [selectedShippingId, setSelectedShippingId] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<CampaignCoupon | null>(null);
  const [couponError, setCouponError] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const submitInFlightRef = useRef(false);
  const navigate = useNavigate();
  // Captured once from the prefill so it survives the navigation-state clear
  // below (location.state is wiped right after mount).
  const [declinedOrderId] = useState<string | undefined>(prefill?.declinedOrderId);

  // Consume the prefill from navigation state once, then clear it from
  // history — a later refresh or back-navigation to this page shouldn't
  // silently re-show the same customer's data again. Runs only on mount:
  // navigate/location.pathname are stable across this component's lifetime.
  useEffect(() => {
    if (prefill) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  const { data: declinedOrderData, isLoading: declinedOrderLoading } = useQuery({
    queryKey: ["order-detail", declinedOrderId],
    queryFn: () => getOrderDetail(declinedOrderId!).then((r) => r.data),
    enabled: !!declinedOrderId,
  });
  const declinedCardInfo = declinedOrderData?.order
    ? extractDeclinedCardInfo(declinedOrderData.order)
    : null;
  const declinedCustomerId = declinedOrderData?.order?.customerId as string | number | undefined;

  // Fetch campaigns
  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ["order-entry-campaigns"],
    queryFn: () => getOrderEntryCampaigns().then((r) => r.data),
  });
  const campaigns: Campaign[] = campaignsData?.campaigns ?? [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaultValues(prefill),
  });

  const sameAsShipping = watch("sameAsShipping");
  const selectedCampaignId = watch("campaignId");
  const country = watch("country");
  const shipCountry = watch("shipCountry");
  const paymentMethod = watch("paymentMethod");

  // Reset state/province when the country changes — US state codes don't apply elsewhere.
  useEffect(() => {
    setValue("state", "");
  }, [country, setValue]);
  useEffect(() => {
    setValue("shipState", "");
  }, [shipCountry, setValue]);

  const {
    data: productsData,
    isLoading: productsLoading,
    isFetching: productsRefreshing,
  } = useQuery({
    queryKey: ["campaign-products", selectedCampaignId],
    queryFn: ({ signal }) =>
      getCampaignProducts(Number(selectedCampaignId), { signal }).then(
        (r) => r.data
      ),
    enabled: !!selectedCampaignId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    refetchOnMount: "always",
  });
  const products: CampaignProduct[] = useMemo(
    () => productsData?.products ?? [],
    [productsData]
  );
  const shippingProfiles: ShippingProfile[] = productsData?.shippingProfiles ?? [];

  const MAX_VISIBLE_PRODUCTS = 50;

  // The search box stays responsive while the (much more expensive) re-filter
  // and row rebuild happen at a lower priority — otherwise each keystroke
  // blocks on rebuilding the table before the character appears.
  const deferredSearch = useDeferredValue(productSearch);

  const filteredProducts = useMemo(() => {
    let result = products;
    if (deferredSearch.trim()) {
      const q = deferredSearch.toLowerCase();
      result = products.filter((p: CampaignProduct) => {
        const name = (p.offerName || p.offerLabel || p.productName || "").toLowerCase();
        return name.includes(q);
      });
    }
    // Limit rendered products to prevent browser freeze
    return result.slice(0, MAX_VISIBLE_PRODUCTS);
  }, [products, deferredSearch]);

  const priceOf = (p: CampaignProduct): number => {
    const override = offerPrices[p.campaignProductId];
    const raw = override !== undefined && override !== "" ? override : p.price;
    const n = parseFloat(raw || "0");
    return Number.isNaN(n) ? 0 : n;
  };

  // Compute totals
  const totals = useMemo(() => {
    let totalPrice = 0;
    let totalShipping = 0;
    for (const p of products) {
      const qty = offerQtys[p.campaignProductId] || 0;
      if (qty > 0) {
        totalPrice += qty * priceOf(p);
        totalShipping += qty * parseFloat(p.shippingPrice || "0");
      }
    }
    // Add shipping profile price if selected
    if (selectedShippingId) {
      const sp = shippingProfiles.find(
        (s: ShippingProfile) => String(s.shippingId) === selectedShippingId
      );
      if (sp) {
        totalShipping += parseFloat(sp.shippingPrice || "0");
      }
    }
    // Calculate discount from applied coupon
    let discount = 0;
    if (appliedCoupon) {
      const applyTo = appliedCoupon.applyTo?.toUpperCase() || "";
      let baseAmount = totalPrice;
      if (applyTo === "SHIPPING") {
        baseAmount = totalShipping;
      } else if (applyTo === "TOTAL") {
        baseAmount = totalPrice + totalShipping;
      }
      if (appliedCoupon.discountPerc) {
        const pct = parseFloat(appliedCoupon.discountPerc);
        // API returns decimal (0.10 = 10%) or whole number (10 = 10%)
        discount = baseAmount * (pct < 1 ? pct : pct / 100);
      } else if (appliedCoupon.discountPrice) {
        discount = parseFloat(appliedCoupon.discountPrice);
      }
      // Don't let discount exceed the base amount
      discount = Math.min(discount, totalPrice + totalShipping);
    }
    const salesTax = 0;
    return {
      totalPrice,
      totalShipping,
      discount,
      salesTax,
      grandTotal: totalPrice + totalShipping - discount + salesTax,
    };
  }, [products, offerQtys, offerPrices, selectedShippingId, shippingProfiles, appliedCoupon]);

  // Build offers string: campaignProductId,productId,qty[,newPrice,originPrice];...
  // CheckoutChamp import uses campaignProductId, checkout URL uses campaignProductId.productId.
  // The 4th (new price) and 5th (origin price, as last displayed on this form)
  // fields are appended only when the agent overrode the campaign price — the
  // origin price lets the backend skip re-fetching the campaign from
  // CheckoutChamp just to check whether the price actually changed.
  function buildOffersString(): string {
    const parts: string[] = [];
    for (const p of products) {
      const qty = offerQtys[p.campaignProductId] || 0;
      if (qty > 0) {
        const override = offerPrices[p.campaignProductId];
        const originPrice = parseFloat(p.price || "0");
        const overridden =
          override !== undefined &&
          override !== "" &&
          !Number.isNaN(Number(override)) &&
          Number(override) !== originPrice;
        if (overridden) {
          parts.push(
            `${p.campaignProductId},${p.productId},${qty},${Number(override)},${originPrice}`
          );
        } else {
          parts.push(`${p.campaignProductId},${p.productId},${qty}`);
        }
      }
    }
    return parts.join(";");
  }

  const handleApplyCoupon = async (couponCode: string) => {
    setCouponError("");
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }
    if (!selectedCampaignId) {
      setCouponError("Select a campaign first");
      return;
    }
    setCouponChecking(true);
    try {
      const res = await lookupCampaignCoupon(
        Number(selectedCampaignId),
        couponCode
      );
      setAppliedCoupon(res.data.coupon);
    } catch (err) {
      const status = (err as AxiosError).response?.status;
      setCouponError(
        status === 404
          ? "Invalid coupon code"
          : "Could not check this coupon — please try again."
      );
    } finally {
      setCouponChecking(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError("");
  };

  // These are passed to every memoized ProductRow, so they must keep a stable
  // identity across renders — otherwise every row's props change on each render
  // and memo never bails out. Functional updates keep the deps empty.
  const setQty = useCallback((offerId: number, delta: number) => {
    setOfferQtys((prev) => {
      const current = prev[offerId] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [offerId]: next };
    });
  }, []);

  const setQtyDirect = useCallback((offerId: number, value: number) => {
    setOfferQtys((prev) => ({
      ...prev,
      [offerId]: Math.max(0, value),
    }));
  }, []);

  const setPriceDirect = useCallback((offerId: number, value: string) => {
    setOfferPrices((prev) => ({ ...prev, [offerId]: value }));
  }, []);

  const mutation = useMutation({
    mutationFn: async (data: FormData): Promise<unknown> => {
      const offersStr = buildOffersString();

      const selectedCampaign = campaigns.find(
        (c: Campaign) => String(c.campaignId) === data.campaignId
      );

      const payload: OrderImportPayload = {
        campaignId: Number(data.campaignId),
        campaignName: selectedCampaign?.campaignName || undefined,
        campaignCategoryName: selectedCampaign?.campaignCategoryName || undefined,
        totalAmount: totals.grandTotal > 0 ? totals.grandTotal : undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber || undefined,
        companyName: data.companyName || undefined,
        address1: data.address1,
        address2: data.address2 || undefined,
        city: data.city,
        state: data.state,
        country: data.country,
        postalCode: data.postalCode,
        paySource: "PREPAID",
        offers: offersStr || undefined,
        couponCode: data.couponCode || undefined,
        notes: data.notes || undefined,
        shippingId: selectedShippingId ? Number(selectedShippingId) : undefined,
      };

      if (data.sameAsShipping) {
        // Copy billing address to shipping
        payload.shipFirstName = data.firstName;
        payload.shipLastName = data.lastName;
        payload.shipAddress1 = data.address1;
        payload.shipAddress2 = data.address2 || undefined;
        payload.shipCity = data.city;
        payload.shipState = data.state;
        payload.shipCountry = data.country;
        payload.shipPostalCode = data.postalCode;
      } else {
        payload.shipFirstName = data.shipFirstName || undefined;
        payload.shipLastName = data.shipLastName || undefined;
        payload.shipAddress1 = data.shipAddress1 || undefined;
        payload.shipAddress2 = data.shipAddress2 || undefined;
        payload.shipCity = data.shipCity || undefined;
        payload.shipState = data.shipState || undefined;
        payload.shipCountry = data.shipCountry || undefined;
        payload.shipPostalCode = data.shipPostalCode || undefined;
      }

      // Card path: charges immediately via order/import/ and creates the order
      // already paid — no invoice email follows. `customerId` is only sent when
      // retrying a declined order, so the charge lands on that same CRM
      // customer; a normal card order has no customer yet and omits it.
      if (data.paymentMethod === "credit_card") {
        return chargeOrder({
          customerId: declinedOrderId ? declinedCustomerId : undefined,
          campaignId: Number(data.campaignId),
          campaignName: payload.campaignName,
          campaignCategoryName: payload.campaignCategoryName,
          offers: offersStr || "",
          emailAddress: data.emailAddress,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber || undefined,
          companyName: data.companyName || undefined,
          address1: data.address1,
          address2: data.address2 || undefined,
          city: data.city,
          state: data.state,
          country: data.country,
          postalCode: data.postalCode,
          shipFirstName: payload.shipFirstName,
          shipLastName: payload.shipLastName,
          shipAddress1: payload.shipAddress1,
          shipAddress2: payload.shipAddress2,
          shipCity: payload.shipCity,
          shipState: payload.shipState,
          shipCountry: payload.shipCountry,
          shipPostalCode: payload.shipPostalCode,
          couponCode: data.couponCode || undefined,
          shippingId: payload.shippingId,
          notes: data.notes || undefined,
          totalAmount: payload.totalAmount,
          cardNumber: (data.creditCardNumber || "").replace(/\D/g, ""),
          cardMonth: data.expirationMonth || "",
          cardYear: data.expirationYear || "",
          cardSecurityCode: data.cvv || "",
        });
      }

      return importOrder(payload);
    },
    onSuccess: (_, variables) => {
      setSuccessMsg(
        variables.paymentMethod === "credit_card" ? "Card charged successfully!" : "Invoice sent!"
      );
      setSuccessSeverity("success");
      setSuccessOpen(true);
      setError("");
      setOfferQtys({});
      setOfferPrices({});
      setSelectedShippingId("");
      setAppliedCoupon(null);
      setCouponError("");
      reset();
      submitInFlightRef.current = false;
    },
    onError: (err: AxiosError<ApiError>) => {
      setError(err.response?.data?.message ?? "Failed to create order");
      submitInFlightRef.current = false;
    },
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Order Entry
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {declinedOrderId && (
        <Alert
          severity="warning"
          icon={<CreditCardIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight={700} mb={0.5}>
            Declined order {declinedOrderId} — retry the charge below with Credit Card, or fall back to External Payment
          </Typography>
          {declinedOrderLoading ? (
            <Typography variant="body2">Loading card info...</Typography>
          ) : declinedCardInfo ? (
            <Typography variant="body2">
              {[
                declinedCardInfo.cardType,
                declinedCardInfo.last4 ? `ending in ${declinedCardInfo.last4}` : null,
                declinedCardInfo.declineReason ? `— declined: ${declinedCardInfo.declineReason}` : null,
              ]
                .filter(Boolean)
                .join(" ") || "No card details found for this order."}
            </Typography>
          ) : (
            <Typography variant="body2">Could not load card info for this order.</Typography>
          )}
        </Alert>
      )}

      <form
        onSubmit={handleSubmit((data) => {
          if (submitInFlightRef.current) return;
          submitInFlightRef.current = true;
          mutation.mutate(data);
        })}
      >
        {/* Campaign selector */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Typography fontWeight={600} sx={{ minWidth: 80 }}>
            Campaign
          </Typography>
          <Controller
            name="campaignId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={campaigns}
                getOptionLabel={(opt) => opt.campaignName}
                loading={campaignsLoading}
                value={
                  campaigns.find(
                    (c: Campaign) => String(c.campaignId) === field.value
                  ) ?? null
                }
                onChange={(_, val) => {
                  field.onChange(val ? String(val.campaignId) : "");
                  setOfferQtys({});
                  setOfferPrices({});
                  setSelectedShippingId("");
                  setAppliedCoupon(null);
                  setCouponError("");
                  setProductSearch("");
                }}
                sx={{ minWidth: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select campaign..."
                    error={!!errors.campaignId}
                    helperText={errors.campaignId?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {campaignsLoading ? (
                            <CircularProgress size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Box>

        {/* Billing Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2.5} color="primary.dark">
            Billing Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>First Name</FieldLabel>
              <TextField {...register("firstName")} error={!!errors.firstName} helperText={errors.firstName?.message} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Last Name</FieldLabel>
              <TextField {...register("lastName")} error={!!errors.lastName} helperText={errors.lastName?.message} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel>Company Name</FieldLabel>
              <TextField {...register("companyName")} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Address Line 1</FieldLabel>
              <TextField {...register("address1")} error={!!errors.address1} helperText={errors.address1?.message} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel>Address Line 2</FieldLabel>
              <TextField {...register("address2")} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>City</FieldLabel>
              <TextField {...register("city")} error={!!errors.city} helperText={errors.city?.message} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Postal Code</FieldLabel>
              <TextField {...register("postalCode")} error={!!errors.postalCode} helperText={errors.postalCode?.message} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>State</FieldLabel>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.state}
                    helperText={errors.state?.message}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>Select state...</MenuItem>
                    {(STATES_BY_COUNTRY[country] ?? []).map((s) => (
                      <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Country</FieldLabel>
              <TextField {...register("country")} error={!!errors.country} helperText={errors.country?.message} fullWidth size="small" select defaultValue="US">
                {COUNTRIES.map((c) => (<MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel>Phone</FieldLabel>
              <TextField {...register("phoneNumber")} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel required>Email</FieldLabel>
              <TextField type="email" {...register("emailAddress")} error={!!errors.emailAddress} helperText={errors.emailAddress?.message} fullWidth size="small" />
            </Grid>
          </Grid>
        </Paper>

        {/* Shipping Address */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2} color="primary.dark">
            Shipping Address
          </Typography>
          <Controller
            name="sameAsShipping"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Same as billing address"
                sx={{ mb: 1 }}
              />
            )}
          />
          {!sameAsShipping && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FieldLabel>First Name</FieldLabel>
                <TextField {...register("shipFirstName")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Last Name</FieldLabel>
                <TextField {...register("shipLastName")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Address Line 1</FieldLabel>
                <TextField {...register("shipAddress1")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Address Line 2</FieldLabel>
                <TextField {...register("shipAddress2")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>City</FieldLabel>
                <TextField {...register("shipCity")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Postal Code</FieldLabel>
                <TextField {...register("shipPostalCode")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>State</FieldLabel>
                <Controller
                  name="shipState"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      fullWidth
                      size="small"
                      select
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>Select state...</MenuItem>
                      {(STATES_BY_COUNTRY[shipCountry ?? ""] ?? []).map((s) => (
                        <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Country</FieldLabel>
                <TextField {...register("shipCountry")} fullWidth size="small" select defaultValue="US">
                  {COUNTRIES.map((c) => (<MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>))}
                </TextField>
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Payment Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2} color="primary.dark">
            Payment Information (CC or Checking)
          </Typography>
          <Divider sx={{ mb: 2, borderStyle: "dashed" }} />
          <Controller
            name="paymentMethod"
            control={control}
            render={({ field }) => (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, mb: paymentMethod === "credit_card" ? 3 : 0 }}>
                <FormControlLabel
                  control={<Radio checked={field.value === "credit_card"} onChange={() => field.onChange("credit_card")} />}
                  label="Credit Card"
                />
                <FormControlLabel control={<Radio disabled />} label="eCheck" sx={{ color: "text.secondary" }} />
                <FormControlLabel
                  control={<Radio checked={field.value === "external"} onChange={() => field.onChange("external")} />}
                  label="External Payment"
                />
              </Box>
            )}
          />
          {paymentMethod === "credit_card" && (
            <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This charges the card immediately via CheckoutChamp — the order is
                    created already paid and <strong>no invoice email is sent</strong>.
                    Confirm with the customer before submitting.
                  </Alert>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>Card Number</FieldLabel>
                      <TextField
                        {...register("creditCardNumber")}
                        error={!!errors.creditCardNumber}
                        helperText={errors.creditCardNumber?.message}
                        fullWidth
                        size="small"
                        inputProps={{ inputMode: "numeric", autoComplete: "cc-number" }}
                        placeholder="4111 1111 1111 1111"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FieldLabel required>Security Code</FieldLabel>
                      <TextField
                        {...register("cvv")}
                        error={!!errors.cvv}
                        helperText={errors.cvv?.message}
                        fullWidth
                        size="small"
                        inputProps={{ inputMode: "numeric", autoComplete: "cc-csc" }}
                        placeholder="123"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FieldLabel required>Expires</FieldLabel>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Controller
                          name="expirationMonth"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} select size="small" error={!!errors.expirationMonth} sx={{ minWidth: 80 }}>
                              <MenuItem value="" disabled>MM</MenuItem>
                              {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                        <Controller
                          name="expirationYear"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} select size="small" error={!!errors.expirationYear} sx={{ minWidth: 90 }}>
                              <MenuItem value="" disabled>YYYY</MenuItem>
                              {Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() + i)).map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
        </Paper>

        {/* Select Products */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Typography variant="h6" fontWeight={600} color="primary.dark">
              Select Products
            </Typography>
            {productsRefreshing && !productsLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Refreshing prices...
                </Typography>
              </Box>
            )}
          </Box>

          {!selectedCampaignId ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Please select a campaign to view available products.
            </Typography>
          ) : productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : products.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No products found for this campaign.
            </Typography>
          ) : (
            <>
              {/* Product search */}
              <TextField
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                fullWidth
                size="small"
                placeholder="Search products..."
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: productSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setProductSearch("")}>
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Offers table */}
              <TableContainer
                sx={{
                  border: `2px solid ${ORANGE}`,
                  borderRadius: 1,
                  maxHeight: 400,
                  mb: 3,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: ORANGE, fontSize: "0.85rem", bgcolor: "background.paper", width: "45%" }}>
                        Offers
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: ORANGE, fontSize: "0.85rem", bgcolor: "background.paper", width: "25%" }}>
                        Quantity
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: ORANGE, fontSize: "0.85rem", bgcolor: "background.paper", width: "15%" }}>
                        Price
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: ORANGE, fontSize: "0.85rem", bgcolor: "background.paper", width: "15%" }}>
                        Shipping
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.map((p: CampaignProduct) => (
                      <ProductRow
                        key={p.campaignProductId ?? p.productId}
                        product={p}
                        qty={offerQtys[p.campaignProductId] || 0}
                        overridePrice={offerPrices[p.campaignProductId]}
                        onQtyDelta={setQty}
                        onQtySet={setQtyDirect}
                        onPriceChange={setPriceDirect}
                      />
                    ))}
                  </TableBody>
                </Table>
              {products.length > MAX_VISIBLE_PRODUCTS && !deferredSearch.trim() && (
                <Typography variant="body2" sx={{ p: 1, textAlign: "center", color: "text.secondary" }}>
                  Showing {MAX_VISIBLE_PRODUCTS} of {products.length} products. Use search to find more.
                </Typography>
              )}
              </TableContainer>

              {/* Coupon + Shipping + Totals */}
              <Grid container spacing={3}>
                {/* Left column: Coupon + Shipping */}
                <Grid item xs={12} md={5}>
                  <FieldLabel>Coupon</FieldLabel>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
                    <TextField
                      {...register("couponCode")}
                      fullWidth
                      size="small"
                      placeholder="Enter coupon code"
                      disabled={!!appliedCoupon}
                      error={!!couponError}
                      helperText={couponError}
                    />
                    <Button
                      variant="contained"
                      onClick={() => handleApplyCoupon(watch("couponCode") || "")}
                      disabled={!!appliedCoupon || couponChecking}
                      startIcon={
                        couponChecking ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                      sx={{
                        bgcolor: "primary.main",
                        color: "#fff",
                        minWidth: 80,
                        height: 40,
                        "&:hover": { bgcolor: "primary.dark" },
                      }}
                    >
                      {couponChecking ? "Checking..." : "Apply"}
                    </Button>
                  </Box>
                  {appliedCoupon && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: "#d32f2f",
                            cursor: "pointer",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                          }}
                          onClick={handleRemoveCoupon}
                        >
                          X
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ color: "#2e7d32", fontWeight: 700, fontSize: "0.85rem" }}
                        >
                          {appliedCoupon.couponCode}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.85rem" }}
                        >
                          {(() => {
                            const target = appliedCoupon.applyTo === "SHIPPING" ? "Shipping" : appliedCoupon.applyTo === "TOTAL" ? "Total" : "Price";
                            if (appliedCoupon.discountPerc) {
                              const pct = parseFloat(appliedCoupon.discountPerc);
                              const display = pct < 1 ? (pct * 100).toFixed(0) : pct.toFixed(0);
                              return `${display}% off on Order ${target}`;
                            }
                            if (appliedCoupon.discountPrice) {
                              return `$${parseFloat(appliedCoupon.discountPrice).toFixed(2)} off on Order ${target}`;
                            }
                            return "";
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <FieldLabel>Shipping</FieldLabel>
                  <TextField
                    value={selectedShippingId}
                    onChange={(e) => setSelectedShippingId(e.target.value)}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      Default Shipping
                    </MenuItem>
                    {shippingProfiles.map((sp: ShippingProfile) => (
                      <MenuItem key={sp.shippingId} value={String(sp.shippingId)}>
                        {sp.shippingName || `Shipping ${sp.shippingId}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Right column: Totals */}
                <Grid item xs={12} md={7}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                      <Typography fontWeight={600} sx={{ minWidth: 130 }}>
                        Total Price
                      </Typography>
                      <TextField
                        value={totals.totalPrice.toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={{ width: 140 }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                      <Typography fontWeight={600} sx={{ minWidth: 130 }}>
                        Total Shipping
                      </Typography>
                      <TextField
                        value={totals.totalShipping.toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={{ width: 140 }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                      <Typography fontWeight={600} sx={{ minWidth: 130 }}>
                        Sales Tax
                      </Typography>
                      <TextField
                        value={totals.salesTax.toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={{ width: 140 }}
                      />
                    </Box>
                    {appliedCoupon && totals.discount > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                        <Typography fontWeight={600} sx={{ minWidth: 130, color: "#2e7d32" }}>
                          Discount
                        </Typography>
                        <TextField
                          value={totals.discount.toFixed(2)}
                          size="small"
                          InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          sx={{ width: 140 }}
                        />
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                      <Typography fontWeight={700} sx={{ minWidth: 130 }}>
                        Grand Total
                      </Typography>
                      <TextField
                        value={totals.grandTotal.toFixed(2)}
                        size="small"
                        InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={{ width: 140, "& .MuiOutlinedInput-root": { fontWeight: 700 } }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>

        {/* Notes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2.5} color="primary.dark">
            Notes
          </Typography>
          <TextField
            {...register("notes")}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Optional notes for this order"
          />
        </Paper>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="flex-end">
          <Button
            type="button"
            color="inherit"
            onClick={() => {
              reset();
              setOfferQtys({});
              setOfferPrices({});
              setSelectedShippingId("");
              setAppliedCoupon(null);
              setCouponError("");
              setProductSearch("");
            }}
            sx={{ mr: 2 }}
            disabled={mutation.isPending}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={paymentMethod === "credit_card" ? <CreditCardIcon /> : <SendIcon />}
            disabled={mutation.isPending}
          >
            {paymentMethod === "credit_card"
              ? mutation.isPending
                ? "Charging..."
                : "Charge Card"
              : mutation.isPending
                ? "Sending..."
                : "Send Invoice"}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={successSeverity}
          onClose={() => setSuccessOpen(false)}
          variant="filled"
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
